import axios, { AxiosRequestConfig, Method } from 'axios';
import { Camelized, camelizeKeys, decamelizeKeys, decamelize as decamelizeKey } from 'humps';
import { get, isNil, isArray } from 'lodash';
import { Map } from 'immutable';
import * as Sentry from '@sentry/react';

import { API_URL, API_VER } from './env';

import { HierarchyType } from 'services/resources';

// REDUX
import ConfiguredStore from './index';
import { receivedUnauthorized, receivedNetworkError } from 'services/Authentication/actions';

const HTTP_FORBIDDEN = 403;
const HTTP_AUTH_REQUIRED = 401;
const HTTP_BAD_REQUEST = 400;
const HTTP_NOT_FOUND = 404;
const HTTP_SERVER_ERROR = 500;
// Set of response codes considered a bad request.
const HTTP_FAILED_REQUESTS = [HTTP_FORBIDDEN, HTTP_AUTH_REQUIRED, HTTP_BAD_REQUEST, HTTP_NOT_FOUND];

export enum AxiosResponseType {
  JSON = 'json',
  ArrayBuffer = 'arraybuffer',
  Blob = 'blob',
  Document = 'document',
  Text = 'text',
  Steam = 'stream',
}

function convertObjectToURLSearchParams(params: object | URLSearchParams): URLSearchParams {
  if (params instanceof URLSearchParams) {
    return params;
  }
  const urlParams = new URLSearchParams();

  if (!params) {
    return urlParams;
  }

  for (const [key, value] of Object.entries(params)) {
    if (!(value === undefined || value === null)) {
      if (value instanceof Date) {
        urlParams.append(key, value.toISOString());
      } else {
        urlParams.append(key, value.toString());
      }
    }
  }
  return urlParams;
}

function appendScopeToParams(params: object | URLSearchParams, requestMethod: Method): URLSearchParams {
  // @ts-expect-error Types for the configured store middleware, doesn't support Immutable.JS getIn
  const scope = ConfiguredStore.getState().getIn(['hierarchy', 'selectedEntity']);
  const newParams = convertObjectToURLSearchParams(params);

  // do not override dealership_scope or acting_as if request has already appended it
  if (scope && requestMethod === 'GET' && !newParams.has('dealership_scope')) {
    newParams.append('dealership_scope', scope);
  } else if (scope !== HierarchyType.HIERARCHY && !newParams.has('acting_as')) {
    newParams.append('acting_as', scope);
  }

  return newParams;
}

/**
 * Requests a URL, returning a promise.
 *
 * Axios by default will reject a promise if it is not between status codes 200-300
 * (This can be modified by implementing the validateStatus method)
 *
 */
function request<T, RT extends AxiosResponseType = AxiosResponseType.JSON>(
  path: string,
  options: {
    method?: Method;
    headers?: object;
    data?: any;
    params?: object;
    noKeyTransform?: string[] | boolean;
    responseType?: RT;
  } = {
    method: 'GET',
    headers: {},
    responseType: AxiosResponseType.JSON as RT,
  },
  apiVer = API_VER
): RT extends AxiosResponseType.Blob ? Promise<T> : Promise<T extends (infer U)[] ? Camelized<U>[] : Camelized<T>> {
  let { data } = options;
  const { params } = options;

  const scopedParams = decamelize(appendScopeToParams(params, options.method));

  if (options.noKeyTransform !== true && !isNil(data) && typeof data === 'object') {
    data = decamelize(data, options.noKeyTransform);
  }

  const additionalHeaders = {};

  try {
    const apiToken = localStorage.getItem('api_token');

    if (!isNil(apiToken)) {
      // @ts-expect-error TS(2339): Property 'Authorization' does not exist on type '{... Remove this comment to see the full error message
      additionalHeaders.Authorization = `Bearer ${apiToken}`;
    }
  } catch {
    // Cookies are disabled
    Sentry.addBreadcrumb({
      level: 'warning',
      category: 'browser',
      message: 'Local Storage is not available.',
    });
  }

  // @ts-expect-error TS(2339): Property 'get' does not exist on type '{ language:... Remove this comment to see the full error message
  const unitSystem = ConfiguredStore.getState().get('fleetSettings').get('settings').get('unitSystem');

  if (unitSystem) {
    additionalHeaders['x-fleet-unit-system'] = unitSystem;
  }

  if (path.startsWith('/')) {
    path = path.substring(1);
  }

  const conf: AxiosRequestConfig = {
    url: `${API_URL}${apiVer}${path}`,
    method: !isNil(options.method) ? options.method : 'GET',
    data,
    params: scopedParams,
    responseType: options.responseType,
    headers: {
      ...additionalHeaders,
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      ...options.headers,
    },
  };

  return axios(conf)
    .then((response) => {
      if (options.responseType !== AxiosResponseType.Blob) {
        return camelizeKeys(response.data, { split: /(?=[A-Z0-9])/ });
      } else {
        return response.data;
      }
    })
    .catch((error) => {
      const response = {
        data: get(error, 'response.data') || [error.message || { translationId: 'errors.unspecified' }],
        status: get(error, 'response.status'),
      };

      if (get(error, 'response.status') === HTTP_AUTH_REQUIRED) {
        ConfiguredStore.dispatch(receivedUnauthorized());
      }

      if (!HTTP_FAILED_REQUESTS.includes(response.status)) {
        ConfiguredStore.dispatch(receivedNetworkError(response));
        // Not supported by all browsers, but works well in
        // modern Webkits, Firefox and Edge.
        const trace = {};
        if (typeof Error.captureStackTrace === 'function') {
          Error.captureStackTrace(trace);
        }

        Sentry.captureException(error);
      }

      return Promise.reject(camelizeKeys(response, { split: /(?=[A-Z0-9])/ }));
    });
}

export default request;

/**
 * Decamelize keys. Optionally accepts a second `noKeyTransform` argument:
 * an array of keys that will be excluded from decamelization. These
 * dataPaths can reference arbitrarily deep sub paths by using period
 * separation e.g. `['customer.name', 'customer.address.line1']`
 */
function decamelize<T extends object>(data: T, noKeyTransform?: string[] | boolean) {
  if (toString.call(data).includes('URLSearchParams')) {
    const actualData = data as URLSearchParams;
    const outputSearchParams = new URLSearchParams();

    for (const [key, value] of actualData.entries()) {
      outputSearchParams.append(decamelizeKey(key), value);
    }

    return outputSearchParams;
  }

  if (isArray(noKeyTransform)) {
    // Use immutable maps to get access to the convenient `deleteIn` property
    let changedKeys = Map(data);

    // Build two maps, one with all the key/values that will be camelized
    // and one with all the ones that wont'be.
    const unchangedKeys = noKeyTransform.reduce((unchangedKeysAccumulator, dataPath) => {
      const editPath = dataPath.split('.');

      const modifiedAccumulator = unchangedKeysAccumulator.setIn(editPath, changedKeys.getIn(editPath));

      changedKeys = changedKeys.deleteIn(editPath);

      return modifiedAccumulator;
    }, Map({}));

    changedKeys = Map(decamelizeKeys(changedKeys.toJS(), { split: /(?=[A-Z0-9])/ }));

    // @ts-expect-error TS(2339): Property 'mergeDeep' does not exist on type 'Colle... Remove this comment to see the full error message
    return changedKeys.mergeDeep(unchangedKeys).toJS();
  }

  // Regex to treat numbers as capitals
  return decamelizeKeys(data, { split: /(?=[A-Z0-9])/ });
}
